import {createApi} from "@reduxjs/toolkit/query/react";
import { fetchBaseQueryWithPrepareHeaders } from "./util";

const BASE_URL = '/api/plamo/'

const baseQuery = fetchBaseQueryWithPrepareHeaders({
  baseUrl: BASE_URL,
})

export const plamoApi = createApi({
  reducerPath: 'plamoApi',
  baseQuery,
  tagTypes: ['Customer', 'Employee', 'Assignment'],
  endpoints: (builder:any) => ({
    getCustomers: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/customer`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Customer', id: 'LIST' }],
    }),
    getCustomer: builder.query({
      query: ({id}) => `/customer/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Customer', id }]
    }),
    createCustomer: builder.mutation({
      query: (customer) => ({
        url: `/customer`,
        method: 'post',
        body: JSON.stringify(customer)
      }),
      invalidatesTags: () => [{ type: 'Customer', id: 'LIST' }]
    }),
    updateCustomer: builder.mutation({
      query: (customer) => ({
        url: `/customer/${customer.id}`,
        method: 'put',
        body: JSON.stringify(customer)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Customer', id: 'LIST' }, { type: 'Customer', id }, { type: 'Budget', id }]
    }),
    deleteCustomer: builder.mutation({
      query: ({id}) => ({
        url: `/customer/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Customer', id: 'LIST' }, { type: 'Customer', id }, { type: 'Budget', id }]
    }),
    getCustomersQuery: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/customer-query`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Customer', id: 'LIST' }],
    }),
    calculateCustomerBudget: builder.query({ 
      query: ({id, year}) => ({
        url: `/customer/${id}/calculate-budget/${year}`,
        method: 'get'
      }),
      providesTags: (_1, _2, {id}) => [{ type: 'Budget', id }, { type: 'Budget', id: 'LIST' }]
    }),

    getBudgetCorrections: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/budget-correction`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'BudgetCorrection', id: 'LIST' }]
    }),
    getBudgetCorrection: builder.query({
      query: ({id}) => `/budget-correction/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'BudgetCorrection', id }]
    }),
    createBudgetCorrection: builder.mutation({
      query: (budgetCorrection) => ({
        url: `/budget-correction`,
        method: 'post',
        body: JSON.stringify(budgetCorrection)
      }),
      invalidatesTags: () => [{ type: 'BudgetCorrection', id: 'LIST' }, { type: 'Budget', id: 'LIST' }]
    }),
    updateBudgetCorrection: builder.mutation({
      query: (budgetCorrection) => ({
        url: `/budget-correction/${budgetCorrection.id}`,
        method: 'put',
        body: JSON.stringify(budgetCorrection)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'BudgetCorrection', id: 'LIST' }, { type: 'BudgetCorrection', id }, { type: 'Budget', id: 'LIST' }]
    }),
    deleteBudgetCorrection: builder.mutation({
      query: ({id}) => ({
        url: `/budget-correction/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'BudgetCorrection', id: 'LIST' }, { type: 'BudgetCorrection', id }, { type: 'Budget', id: 'LIST' }]
    }),


    getEmployees: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/employee`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Employee', id: 'LIST' }]
    }),
    getEmployee: builder.query({
      query: ({id}) => `/employee/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Employee', id }]
    }),
    createEmployee: builder.mutation({
      query: (employee) => ({
        url: `/employee`,
        method: 'post',
        body: JSON.stringify(employee)
      }),
      invalidatesTags: () => [{ type: 'Employee', id: 'LIST' }]
    }),
    updateEmployee: builder.mutation({
      query: (employee) => ({
        url: `/employee/${employee.id}`,
        method: 'put',
        body: JSON.stringify(employee)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Employee', id: 'LIST' }, { type: 'Employee', id }]
    }),
    deleteEmployee: builder.mutation({
      query: ({id}) => ({
        url: `/employee/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Employee', id: 'LIST' }, { type: 'Employee', id }]
    }),
    resetPassword: builder.mutation({
      query: ({id}) => ({
        url: `/employee/${id}/reset-password`,
        method: 'post'
      }),
    }),
    getAssignments: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/assignment`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Assignment', id: 'LIST' }]
    }),
    getAssignment: builder.query({
      query: ({id}) => `/assignment/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Assignment', id }]
    }),
    createAssignment: builder.mutation({
      query: (assignment) => ({
        url: `/assignment`,
        method: 'post',
        body: JSON.stringify(assignment)
      }),
      invalidatesTags: () => [{ type: 'Assignment', id: 'LIST' }]
    }),
    updateAssignment: builder.mutation({
      query: (assignment) => ({
        url: `/assignment/${assignment.id}`,
        method: 'put',
        body: JSON.stringify(assignment)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Assignment', id: 'LIST' }, { type: 'Assignment', id }]
    }),
    deleteAssignment: builder.mutation({
      query: ({id}) => ({
        url: `/assignment/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Assignment', id: 'LIST' }, { type: 'Assignment', id }]
    }),
    getRelatives: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/relative`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Relative', id: 'LIST' }]
    }),
    getRelative: builder.query({
      query: ({id}) => `/relative/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Relative', id }]
    }),
    createRelative: builder.mutation({
      query: (relative) => ({
        url: `/relative`,
        method: 'post',
        body: JSON.stringify(relative)
      }),
      invalidatesTags: () => [{ type: 'Relative', id: 'LIST' }]
    }),
    updateRelative: builder.mutation({
      query: (assignment) => ({
        url: `/relative/${assignment.id}`,
        method: 'put',
        body: JSON.stringify(assignment)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Relative', id: 'LIST' }, { type: 'Relative', id }]
    }),
    deleteRelative: builder.mutation({
      query: ({id}) => ({
        url: `/relative/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Relative', id: 'LIST' }, { type: 'Relative', id }]
    }),

    getComments: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/comment`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Comment', id: 'LIST' }]
    }),
    getComment: builder.query({
      query: ({id}) => `/comment/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Comment', id }]
    }),
    createComment: builder.mutation({
      query: (comment) => ({
        url: `/comment`,
        method: 'post',
        body: JSON.stringify(comment)
      }),
      invalidatesTags: () => [{ type: 'Comment', id: 'LIST' }]
    }),
    updateComment: builder.mutation({
      query: (comment) => ({
        url: `/comment/${comment.id}`,
        method: 'put',
        body: JSON.stringify(comment)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Comment', id: 'LIST' }, { type: 'Comment', id }]
    }),
    deleteComment: builder.mutation({
      query: ({id}) => ({
        url: `/comment/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Comment', id: 'LIST' }, { type: 'Comment', id }]
    }),

    getTasks: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/task`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Task', id: 'LIST' }]
    }),
    getTask: builder.query({
      query: ({id}) => `/task/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Task', id }]
    }),
    createTask: builder.mutation({
      query: (task) => ({
        url: `/task`,
        method: 'post',
        body: JSON.stringify(task)
      }),
      invalidatesTags: () => [{ type: 'Task', id: 'LIST' }]
    }),
    updateTask: builder.mutation({
      query: (task) => ({
        url: `/task/${task.id}`,
        method: 'put',
        body: JSON.stringify(task)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Task', id: 'LIST' }, { type: 'Task', id }]
    }),
    deleteTask: builder.mutation({
      query: ({id}) => ({
        url: `/task/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Task', id: 'LIST' }, { type: 'Task', id }]
    }),

    getTasksQuery: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/task-query`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Task', id: 'LIST' }]
    }),

    getContractTemplates: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/contract-template`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'ContractTemplate', id: 'LIST' }]
    }),
    getContractTemplate: builder.query({
      query: ({id}) => `/contract-template/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'ContractTemplate', id }]
    }),
    createContractTemplate: builder.mutation({
      query: (contractTemplate) => ({
        url: `/contract-template`,
        method: 'post',
        body: JSON.stringify(contractTemplate)
      }),
      invalidatesTags: () => [{ type: 'ContractTemplate', id: 'LIST' }]
    }),
    updateContractTemplate: builder.mutation({
      query: (contractTemplate) => ({
        url: `/contract-template/${contractTemplate.id}`,
        method: 'put',
        body: JSON.stringify(contractTemplate)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'ContractTemplate', id: 'LIST' }, { type: 'ContractTemplate', id }]
    }),
    deleteContractTemplate: builder.mutation({
      query: ({id}) => ({
        url: `/contract-template/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'ContractTemplate', id: 'LIST' }, { type: 'ContractTemplate', id }]
    }),
    getContracts: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/contract`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Contract', id: 'LIST' }]
    }),
    getContract: builder.query({
      query: ({id}) => `/contract/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Contract', id }]
    }),
    createContract: builder.mutation({
      query: (contractTemplate) => ({
        url: `/contract`,
        method: 'post',
        body: JSON.stringify(contractTemplate)
      }),
      invalidatesTags: () => [{ type: 'Contract', id: 'LIST' }]
    }),
    updateContract: builder.mutation({
      query: (contractTemplate) => ({
        url: `/contract/${contractTemplate.id}`,
        method: 'put',
        body: JSON.stringify(contractTemplate)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Contract', id: 'LIST' }, { type: 'Contract', id }]
    }),
    deleteContract: builder.mutation({
      query: ({id}) => ({
        url: `/contract/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Contract', id: 'LIST' }, { type: 'Contract', id }]
    }),
    uploadContractUnsignedPdf: builder.mutation({
      query: ({id, data}) => ({
        url: `/contract/${id}/unsigned-pdf`,
        method: 'put',
        body: data,
        headers: {
          'Content-Type': "application/pdf"
        }
      })
    }),
    downloadContractUnsignedPdf: builder.query({
      query: ({id}) => ({
        url: `/contract/${id}/unsigned-pdf`,
        method: 'get'
      })
    }),
    getSettings: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/setting`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Setting', id: 'LIST' }]
    }),
    getSetting: builder.query({
      query: ({id}) => `/setting/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Setting', id }]
    }),
    createSetting: builder.mutation({
      query: (setting) => ({
        url: `/setting`,
        method: 'post',
        body: JSON.stringify(setting)
      }),
      invalidatesTags: () => [{ type: 'Setting', id: 'LIST' }]
    }),
    updateSetting: builder.mutation({
      query: (setting) => ({
        url: `/setting/${setting.id}`,
        method: 'put',
        body: JSON.stringify(setting)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Setting', id: 'LIST' }, { type: 'Setting', id }]
    }),
    deleteSetting: builder.mutation({
      query: ({id}) => ({
        url: `/setting/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Setting', id: 'LIST' }, { type: 'Setting', id }]
    }),
    getEnums: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/enum`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Enum', id: 'LIST' }]
    }),
    getEnum: builder.query({
      query: ({id}) => `/enum/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Enum', id }]
    }),
    createEnum: builder.mutation({
      query: (theEnum) => ({
        url: `/enum`,
        method: 'post',
        body: JSON.stringify(theEnum)
      }),
      invalidatesTags: () => [{ type: 'Enum', id: 'LIST' }]
    }),
    updateEnum: builder.mutation({
      query: (theEnum) => ({
        url: `/enum/${theEnum.id}`,
        method: 'put',
        body: JSON.stringify(theEnum)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Enum', id: 'LIST' }, { type: 'Enum', id }]
    }),
    deleteEnum: builder.mutation({
      query: ({id}) => ({
        url: `/enum/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Enum', id: 'LIST' }, { type: 'Enum', id }]
    }),
    getEnumItems: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/enum-item`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'EnumItem', id: 'LIST' }]
    }),
    getEnumItem: builder.query({
      query: ({id}) => `/enum-item/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'EnumItem', id }]
    }),
    createEnumItem: builder.mutation({
      query: (enumItem) => ({
        url: `/enum-item`,
        method: 'post',
        body: JSON.stringify(enumItem)
      }),
      invalidatesTags: () => [{ type: 'EnumItem', id: 'LIST' }]
    }),
    updateEnumItem: builder.mutation({
      query: (enumItem) => ({
        url: `/enum-item/${enumItem.id}`,
        method: 'put',
        body: JSON.stringify(enumItem)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'EnumItem', id: 'LIST' }, { type: 'EnumItem', id }]
    }),
    deleteEnumItem: builder.mutation({
      query: ({id}) => ({
        url: `/enum-item/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'EnumItem', id: 'LIST' }, { type: 'EnumItem', id }]
    }),

    getWorkTimes: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/work-time`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'WorkTime', id: 'LIST' }]
    }),

    getWorkTimes2: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/work-time-query`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'WorkTime', id: 'LIST' }]
    }),

    getWorkTime: builder.query({
      query: ({id}) => `/work-time/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'WorkTime', id }]
    }),
    createWorkTime: builder.mutation({
      query: (workTime) => ({
        url: `/work-time`,
        method: 'post',
        body: JSON.stringify(workTime)
      }),
      invalidatesTags: () => [{ type: 'WorkTime', id: 'LIST' }, { type: 'Budget' }]
    }),
    updateWorkTime: builder.mutation({
      query: (workTime) => ({
        url: `/work-time/${workTime.id}`,
        method: 'put',
        body: JSON.stringify(workTime)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'WorkTime', id: 'LIST' }, { type: 'WorkTime', id }, { type: 'Task', id: 'LIST' }, { type: 'Budget' }]
    }),
    deleteWorkTime: builder.mutation({
      query: ({id}) => ({
        url: `/work-time/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'WorkTime', id: 'LIST' }, { type: 'WorkTime', id }, { type: 'Task', id: 'LIST' }, { type: 'Budget' }]
    }),

    getAbsences: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/absence`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Absence', id: 'LIST' }]
    }),
    getAbsence: builder.query({
      query: ({id}) => `/absence/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Absence', id }]
    }),
    createAbsence: builder.mutation({
      query: (absence) => ({
        url: `/absence`,
        method: 'post',
        body: JSON.stringify(absence)
      }),
      invalidatesTags: () => [{ type: 'Absence', id: 'LIST' }]
    }),
    updateAbsence: builder.mutation({
      query: (absence) => ({
        url: `/absence/${absence.id}`,
        method: 'put',
        body: JSON.stringify(absence)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Absence', id: 'LIST' }, { type: 'Absence', id }]
    }),
    deleteAbsence: builder.mutation({
      query: ({id}) => ({
        url: `/absence/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Absence', id: 'LIST' }, { type: 'Absence', id }]
    }),

    getAbsencesQuery: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/absence-query`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Absence', id: 'LIST' }]
    }),
    getAbsenceQuery: builder.query({
      query: ({id}) => `/absence-query/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Absence', id }]
    }),

    getPriceOverwrites: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/price-overwrite`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'PriceOverwrite', id: 'LIST' }]
    }),
    getPriceOverwrite: builder.query({
      query: ({id}) => `/price-overwrite/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'PriceOverwrite', id }]
    }),
    createPriceOverwrite: builder.mutation({
      query: (priceOverwrite) => ({
        url: `/price-overwrite`,
        method: 'post',
        body: JSON.stringify(priceOverwrite)
      }),
      invalidatesTags: () => [{ type: 'PriceOverwrite', id: 'LIST' }]
    }),
    updatePriceOverwrite: builder.mutation({
      query: (priceOverwrite) => ({
        url: `/price-overwrite/${priceOverwrite.id}`,
        method: 'put',
        body: JSON.stringify(priceOverwrite)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'PriceOverwrite', id: 'LIST' }, { type: 'PriceOverwrite', id }]
    }),
    deletePriceOverwrite: builder.mutation({
      query: ({id}) => ({
        url: `/price-overwrite/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'PriceOverwrite', id: 'LIST' }, { type: 'PriceOverwrite', id }]
    }),


    getCounselings: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/counseling`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Counseling', id: 'LIST' }]
    }),
    getCounseling: builder.query({
      query: ({id}) => `/counseling/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Counseling', id }]
    }),
    createCounseling: builder.mutation({
      query: (counseling) => ({
        url: `/counseling`,
        method: 'post',
        body: JSON.stringify(counseling)
      }),
      invalidatesTags: () => [{ type: 'Counseling', id: 'LIST' }]
    }),
    updateCounseling: builder.mutation({
      query: (counseling) => ({
        url: `/counseling/${counseling.id}`,
        method: 'put',
        body: JSON.stringify(counseling)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Counseling', id: 'LIST' }, { type: 'Counseling', id }]
    }),
    deleteCounseling: builder.mutation({
      query: ({id}) => ({
        url: `/counseling/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Counseling', id: 'LIST' }, { type: 'Counseling', id }]
    }),

    getDomesticAids: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/domestic-aid`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'DomesticAid', id: 'LIST' }]
    }),
    getDomesticAid: builder.query({
      query: ({id}) => `/domestic-aid/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'DomesticAid', id }]
    }),
    createDomesticAid: builder.mutation({
      query: (domesticAid) => ({
        url: `/domestic-aid`,
        method: 'post',
        body: JSON.stringify(domesticAid)
      }),
      invalidatesTags: () => [{ type: 'DomesticAid', id: 'LIST' }]
    }),
    updateDomesticAid: builder.mutation({
      query: (domesticAid) => ({
        url: `/domestic-aid/${domesticAid.id}`,
        method: 'put',
        body: JSON.stringify(domesticAid)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'DomesticAid', id: 'LIST' }, { type: 'DomesticAid', id }]
    }),
    deleteDomesticAid: builder.mutation({
      query: ({id}) => ({
        url: `/domestic-aid/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'DomesticAid', id: 'LIST' }, { type: 'DomesticAid', id }]
    }),

    getTeams: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/team`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Team', id: 'LIST' }]
    }),
    getTeam: builder.query({
      query: ({id}) => `/team/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Team', id }]
    }),
    createTeam: builder.mutation({
      query: (team) => ({
        url: `/team`,
        method: 'post',
        body: JSON.stringify(team)
      }),
      invalidatesTags: () => [{ type: 'Team', id: 'LIST' }]
    }),
    updateTeam: builder.mutation({
      query: (team) => ({
        url: `/team/${team.id}`,
        method: 'put',
        body: JSON.stringify(team)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Team', id: 'LIST' }, { type: 'Team', id }]
    }),
    deleteTeam: builder.mutation({
      query: ({id}) => ({
        url: `/team/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Team', id: 'LIST' }, { type: 'Team', id }]
    }),

    getEmployeeClosures: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/employee-closure`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'EmployeeClosure', id: 'LIST' }]
    }),
    getEmployeeClosure: builder.query({
      query: ({id}) => `/employee-closure/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'EmployeeClosure', id }]
    }),
    createEmployeeClosure: builder.mutation({
      query: (employeeClosure) => ({
        url: `/employee-closure`,
        method: 'post',
        body: JSON.stringify(employeeClosure)
      }),
      invalidatesTags: () => [{ type: 'EmployeeClosure', id: 'LIST' }]
    }),
    updateEmployeeClosure: builder.mutation({
      query: (employeeClosure) => ({
        url: `/employee-closure/${employeeClosure.id}`,
        method: 'put',
        body: JSON.stringify(employeeClosure)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'EmployeeClosure', id: 'LIST' }, { type: 'EmployeeClosure', id }]
    }),
    deleteEmployeeClosure: builder.mutation({
      query: ({id}) => ({
        url: `/employee-closure/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'EmployeeClosure', id: 'LIST' }, { type: 'EmployeeClosure', id }]
    }),

    getEmployeeClosureQueries: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/employee-closure-query`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'EmployeeClosure', id: 'LIST' }]
    }),
    getEmployeeClosureQuery: builder.query({
      query: ({id}) => `/employee-closure-query/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'EmployeeClosure', id }]
    }),
    

    getEmployeeClosureByMonths: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/employee-closure-by-month`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'EmployeeClosureByMonth', id: 'LIST' }]
    }),
    getEmployeeClosureByMonth: builder.query({
      query: ({id}) => `/employee-closure-by-month/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'EmployeeClosureByMonth', id }]
    }),

    getWorkTimeEmployeeClosuresCrossReferences: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/work-time-employee-closure-cross-reference`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'WorkTimeEmployeeClosureCrossReference', id: 'LIST' }]
    }),
    getWorkTimeEmployeeClosuresCrossReference: builder.query({
      query: ({id}) => `/work-time-employee-closure-cross-reference/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'WorkTimeEmployeeClosureCrossReference', id }]
    }),
    createWorkTimeEmployeeClosuresCrossReference: builder.mutation({
      query: (workTimeEmployeeClosureCrossReference) => ({
        url: `/work-time-employee-closure-cross-reference`,
        method: 'post',
        body: JSON.stringify(workTimeEmployeeClosureCrossReference)
      }),
      invalidatesTags: () => [{ type: 'WorkTimeEmployeeClosureCrossReference', id: 'LIST' }, { type: 'WorkTime', id: 'LIST' }]
    }),
    updateWorkTimeEmployeeClosuresCrossReference: builder.mutation({
      query: (workTimeEmployeeClosureCrossReference) => ({
        url: `/work-time-employee-closure-cross-reference/${workTimeEmployeeClosureCrossReference.id}`,
        method: 'put',
        body: JSON.stringify(workTimeEmployeeClosureCrossReference)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'WorkTimeEmployeeClosureCrossReference', id: 'LIST' }, { type: 'WorkTimeEmployeeClosureCrossReference', id }]
    }),
    deleteWorkTimeEmployeeClosuresCrossReference: builder.mutation({
      query: ({id}) => ({
        url: `/work-time-employee-closure-cross-reference/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'WorkTimeEmployeeClosureCrossReference', id: 'LIST' }, { type: 'WorkTimeEmployeeClosureCrossReference', id }]
    }),

    getAbsenceEmployeeClosuresCrossReferences: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/absence-employee-closure-cross-reference`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'AbsenceEmployeeClosureCrossReference', id: 'LIST' }]
    }),
    getAbsenceEmployeeClosuresCrossReference: builder.query({
      query: ({id}) => `/absence-employee-closure-cross-reference/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'AbsenceEmployeeClosureCrossReference', id }]
    }),
    createAbsenceEmployeeClosuresCrossReference: builder.mutation({
      query: (absenceEmployeeClosureCrossReference) => ({
        url: `/absence-employee-closure-cross-reference`,
        method: 'post',
        body: JSON.stringify(absenceEmployeeClosureCrossReference)
      }),
      invalidatesTags: () => [{ type: 'AbsenceEmployeeClosureCrossReference', id: 'LIST' }, { type: 'WorkTime', id: 'LIST' }]
    }),
    updateAbsenceEmployeeClosuresCrossReference: builder.mutation({
      query: (absenceEmployeeClosureCrossReference) => ({
        url: `/absence-employee-closure-cross-reference/${absenceEmployeeClosureCrossReference.id}`,
        method: 'put',
        body: JSON.stringify(absenceEmployeeClosureCrossReference)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'AbsenceEmployeeClosureCrossReference', id: 'LIST' }, { type: 'AbsenceEmployeeClosureCrossReference', id }]
    }),
    deleteAbsenceEmployeeClosuresCrossReference: builder.mutation({
      query: ({id}) => ({
        url: `/absence-employee-closure-cross-reference/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'AbsenceEmployeeClosureCrossReference', id: 'LIST' }, { type: 'AbsenceEmployeeClosureCrossReference', id }]
    }),


    getInsurances: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/insurance`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Insurance', id: 'LIST' }]
    }),
    getInsurance: builder.query({
      query: ({id}) => `insurance/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Insurance', id }]
    }),
    createInsurance: builder.mutation({
      query: (insurance) => ({
        url: `/insurance`,
        method: 'post',
        body: JSON.stringify(insurance)
      }),
      invalidatesTags: () => [{ type: 'Insurance', id: 'LIST' }]
    }),
    updateInsurance: builder.mutation({
      query: (insurance) => ({
        url: `/insurance/${insurance.id}`,
        method: 'put',
        body: JSON.stringify(insurance)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Insurance', id: 'LIST' }, { type: 'Insurance', id }]
    }),
    deleteInsurance: builder.mutation({
      query: ({id}) => ({
        url: `/insurance/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Insurance', id: 'LIST' }, { type: 'Insurance', id }]
    }),

    getInsuranceAddresses: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/insurance-address`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'InsuranceAddress', id: 'LIST' }]
    }),
    getInsuranceAddress: builder.query({
      query: ({id}) => `/insurance-address/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'InsuranceAddress', id }]
    }),
    createInsuranceAddress: builder.mutation({
      query: (insuranceAddress) => ({
        url: `/insurance-address`,
        method: 'post',
        body: JSON.stringify(insuranceAddress)
      }),
      invalidatesTags: () => [{ type: 'InsuranceAddress', id: 'LIST' }]
    }),
    updateInsuranceAddress: builder.mutation({
      query: (insuranceAddress) => ({
        url: `/insurance-address/${insuranceAddress.id}`,
        method: 'put',
        body: JSON.stringify(insuranceAddress)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'InsuranceAddress', id: 'LIST' }, { type: 'InsuranceAddress', id }]
    }),
    deleteInsuranceAddress: builder.mutation({
      query: ({id}) => ({
        url: `/insurance-address/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'InsuranceAddress', id: 'LIST' }, { type: 'InsuranceAddress', id }]
    }),

    getInvoices: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/invoice`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Invoice', id: 'LIST' }]
    }),
    getInvoice: builder.query({
      query: ({id}) => `/invoice/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Invoice', id }]
    }),
    createInvoice: builder.mutation({
      query: (invoice) => ({
        url: `/invoice`,
        method: 'post',
        body: JSON.stringify(invoice)
      }),
      invalidatesTags: () => [{ type: 'Invoice', id: 'LIST' }]
    }),
    updateInvoice: builder.mutation({
      query: (invoice) => ({
        url: `/invoice/${invoice.id}`,
        method: 'put',
        body: JSON.stringify(invoice)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Invoice', id: 'LIST' }, { type: 'Invoice', id }]
    }),
    deleteInvoice: builder.mutation({
      query: ({id}) => ({
        url: `/invoice/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Invoice', id: 'LIST' }, { type: 'Invoice', id }]
    }),

    getPublicHolidays: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/public-holiday`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'PublicHoliday', id: 'LIST' }]
    }),
    getPublicHoliday: builder.query({
      query: ({id}) => `/public-holiday/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'PublicHoliday', id }]
    }),
    createPublicHoliday: builder.mutation({
      query: (publicHoliday) => ({
        url: `/public-holiday`,
        method: 'post',
        body: JSON.stringify(publicHoliday)
      }),
      invalidatesTags: () => [{ type: 'PublicHoliday', id: 'LIST' }]
    }),
    updatePublicHoliday: builder.mutation({
      query: (publicHoliday) => ({
        url: `/public-holiday/${publicHoliday.id}`,
        method: 'put',
        body: JSON.stringify(publicHoliday)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'PublicHoliday', id: 'LIST' }, { type: 'PublicHoliday', id }]
    }),
    deletePublicHoliday: builder.mutation({
      query: ({id}) => ({
        url: `/public-holiday/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'PublicHoliday', id: 'LIST' }, { type: 'PublicHoliday', id }]
    }),

    getSalaries: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/salary`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Salary', id: 'LIST' }]
    }),
    getSalary: builder.query({
      query: ({id}) => `/salary/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Salary', id }]
    }),
    createSalary: builder.mutation({
      query: (salary) => ({
        url: `/salary`,
        method: 'post',
        body: JSON.stringify(salary)
      }),
      invalidatesTags: () => [{ type: 'Salary', id: 'LIST' }]
    }),
    updateSalary: builder.mutation({
      query: (salary) => ({
        url: `/salary/${salary.id}`,
        method: 'put',
        body: JSON.stringify(salary)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Salary', id: 'LIST' }, { type: 'Salary', id }]
    }),
    deleteSalary: builder.mutation({
      query: ({id}) => ({
        url: `/salary/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Salary', id: 'LIST' }, { type: 'Salary', id }]
    }),

    getNoVisitReasons: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/no-visit-reason`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'NoVisitReason', id: 'LIST' }]
    }),
    getNoVisitReason: builder.query({
      query: ({id}) => `/no-visit-reason/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'NoVisitReason', id }]
    }),
    createNoVisitReason: builder.mutation({
      query: (noVisitReason) => ({
        url: `/no-visit-reason`,
        method: 'post',
        body: JSON.stringify(noVisitReason)
      }),
      invalidatesTags: () => [{ type: 'NoVisitReason', id: 'LIST' }]
    }),
    updateNoVisitReason: builder.mutation({
      query: (noVisitReason) => ({
        url: `/no-visit-reason/${noVisitReason.id}`,
        method: 'put',
        body: JSON.stringify(noVisitReason)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'NoVisitReason', id: 'LIST' }, { type: 'NoVisitReason', id }]
    }),
    deleteNoVisitReason: builder.mutation({
      query: ({id}) => ({
        url: `/no-visit-reason/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'NoVisitReason', id: 'LIST' }, { type: 'NoVisitReason', id }]
    }),

    getWorkTimeSignatures: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/work-time-signature`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'WorkTimeSignature', id: 'LIST' }]
    }),
    getWorkTimeSignature: builder.query({
      query: ({id}) => `work-time-signature/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'WorkTimeSignature', id }]
    }),
    createWorkTimeSignature: builder.mutation({
      query: (workTimeSignature) => ({
        url: `/work-time-signature`,
        method: 'post',
        body: JSON.stringify(workTimeSignature)
      }),
      invalidatesTags: () => [{ type: 'WorkTimeSignature', id: 'LIST' }, { type: 'WorkTime', id: 'LIST' }]
    }),
    updateWorkTimeSignature: builder.mutation({
      query: (workTimeSignature) => ({
        url: `/work-time-signature/${workTimeSignature.id}`,
        method: 'put',
        body: JSON.stringify(workTimeSignature)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'WorkTimeSignature', id: 'LIST' }, { type: 'WorkTimeSignature', id }]
    }),
    deleteWorkTimeSignature: builder.mutation({
      query: ({id}) => ({
        url: `/work-time-signature/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'WorkTimeSignature', id: 'LIST' }, { type: 'WorkTimeSignature', id }, { type: 'WorkTime', id: 'LIST' }]
    }),

    getQualifications: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/qualification`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Qualification', id: 'LIST' }]
    }),
    getQualification: builder.query({
      query: ({id}) => `qualification/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Qualification', id }]
    }),
    createQualification: builder.mutation({
      query: (workTimeSignature) => ({
        url: `/qualification`,
        method: 'post',
        body: JSON.stringify(workTimeSignature)
      }),
      invalidatesTags: () => [{ type: 'Qualification', id: 'LIST' }]
    }),
    updateQualification: builder.mutation({
      query: (workTimeSignature) => ({
        url: `/qualification/${workTimeSignature.id}`,
        method: 'put',
        body: JSON.stringify(workTimeSignature)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Qualification', id: 'LIST' }, { type: 'Qualification', id }]
    }),
    deleteQualification: builder.mutation({
      query: ({id}) => ({
        url: `/qualification/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Qualification', id: 'LIST' }, { type: 'Qualification', id }]
    }),

    getCalendarEntries: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/calendar-entry`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'CalendarEntry', id: 'LIST' }]
    }),
    getCalendarEntry: builder.query({
      query: ({id}) => `calendar-entry/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'CalendarEntry', id }]
    }),
    createCalendarEntry: builder.mutation({
      query: (workTimeSignature) => ({
        url: `/calendar-entry`,
        method: 'post',
        body: JSON.stringify(workTimeSignature)
      }),
      invalidatesTags: () => [{ type: 'CalendarEntry', id: 'LIST' }]
    }),
    updateCalendarEntry: builder.mutation({
      query: (workTimeSignature) => ({
        url: `/calendar-entry/${workTimeSignature.id}`,
        method: 'put',
        body: JSON.stringify(workTimeSignature)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'CalendarEntry', id: 'LIST' }, { type: 'CalendarEntry', id }]
    }),
    deleteCalendarEntry: builder.mutation({
      query: ({id}) => ({
        url: `/calendar-entry/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'CalendarEntry', id: 'LIST' }, { type: 'CalendarEntry', id }]
    }),

    getCalendarSerieses: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/calendar-series`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'CalendarSeries', id: 'LIST' }]
    }),
    getCalendarSeries: builder.query({
      query: ({id}) => `/calendar-series/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'CalendarSeries', id }]
    }),
    createCalendarSeries: builder.mutation({
      query: (calendarSeries) => ({
        url: `/calendar-series`,
        method: 'post',
        body: JSON.stringify(calendarSeries)
      }),
      invalidatesTags: () => [{ type: 'CalendarSeries', id: 'LIST' }, { type: 'CalendarEntry', id: 'LIST' }]
    }),
    updateCalendarSeries: builder.mutation({
      query: (calendarSeries) => ({
        url: `/calendar-series/${calendarSeries.id}`,
        method: 'put',
        body: JSON.stringify(calendarSeries)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'CalendarSeries', id: 'LIST' }, { type: 'CalendarSeries', id }, { type: 'CalendarEntry', id: 'LIST' }]
    }),
    deleteCalendarSeries: builder.mutation({
      query: ({id}) => ({
        url: `/calendar-series/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'CalendarSeries', id: 'LIST' }, { type: 'CalendarSeries', id }, { type: 'CalendarEntry', id: 'LIST' }]
    }),

    getLocations: builder.query({
      query: ({ c, o, f, st, s }) => ({ 
        url: `/location`,
        params: {
          c, o, f, st, s
        }
      }),
      transformResponse: (response, meta) => ({
        items: response,
        totalCnt: parseInt(meta.response.headers.get('x-pagination-totalcnt')),
        offset: parseInt(meta.response.headers.get('x-pagination-offset')),
        pageSize: parseInt(meta.response.headers.get('x-pagination-pagesize')),
      }),
      providesTags: () => [{ type: 'Location', id: 'LIST' }]
    }),
    getLocation: builder.query({
      query: ({id}) => `/location/${id}`,
      providesTags: (_1, _2, {id}) => [{ type: 'Location', id }]
    }),
    createLocation: builder.mutation({
      query: (location) => ({
        url: `/location`,
        method: 'post',
        body: JSON.stringify(location)
      }),
      invalidatesTags: () => [{ type: 'Location', id: 'LIST' }]
    }),
    updateLocation: builder.mutation({
      query: (location) => ({
        url: `/location/${location.id}`,
        method: 'put',
        body: JSON.stringify(location)
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Location', id: 'LIST' }, { type: 'Location', id }]
    }),
    deleteLocation: builder.mutation({
      query: ({id}) => ({
        url: `/location/${id}`,
        method: 'delete'
      }),
      invalidatesTags: (_1, _2, {id}) => [{ type: 'Location', id: 'LIST' }, { type: 'Location', id }]
    }),
  })

  
})

export const { 
  useGetCustomersQuery,
  useGetCustomerQuery,
  useLazyGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomersQueryQuery,
  useCalculateCustomerBudgetQuery,
  useGetBudgetCorrectionsQuery,
  useGetBudgetCorrectionQuery,
  useCreateBudgetCorrectionMutation,
  useUpdateBudgetCorrectionMutation,
  useDeleteBudgetCorrectionMutation,
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useResetPasswordMutation,
  useGetAssignmentsQuery,
  useGetAssignmentQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
  useGetRelativesQuery,
  useGetRelativeQuery,
  useCreateRelativeMutation,
  useUpdateRelativeMutation,
  useDeleteRelativeMutation,
  useGetCommentsQuery,
  useGetCommentQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetTasksQueryQuery,
  useGetContractTemplatesQuery,
  useGetContractTemplateQuery,
  useCreateContractTemplateMutation,
  useUpdateContractTemplateMutation,
  useDeleteContractTemplateMutation,
  useGetContractsQuery,
  useGetContractQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useDeleteContractMutation,
  useGetSettingsQuery,
  useGetSettingQuery,
  useCreateSettingMutation,
  useUpdateSettingMutation,
  useDeleteSettingMutation,
  useUploadContractUnsignedPdfMutation,
  useDownloadContractUnsignedPdfQuery,
  useGetEnumsQuery,
  useGetEnumQuery,
  useCreateEnumMutation,
  useUpdateEnumMutation,
  useDeleteEnumMutation,
  useGetEnumItemsQuery,
  useGetEnumItemQuery,
  useCreateEnumItemMutation,
  useUpdateEnumItemMutation,
  useDeleteEnumItemMutation,
  useGetWorkTimesQuery,
  useGetWorkTimes2Query,
  useGetWorkTimeQuery,
  useCreateWorkTimeMutation,
  useUpdateWorkTimeMutation,
  useDeleteWorkTimeMutation,
  useGetAbsencesQuery,
  useGetAbsenceQuery,
  useCreateAbsenceMutation,
  useUpdateAbsenceMutation,
  useDeleteAbsenceMutation,
  useGetAbsencesQueryQuery,
  useGetAbsenceQueryQuery,
  useGetPriceOverwritesQuery,
  useGetPriceOverwriteQuery,
  useCreatePriceOverwriteMutation,
  useUpdatePriceOverwriteMutation,
  useDeletePriceOverwriteMutation,
  useGetCounselingsQuery,
  useGetCounselingQuery,
  useCreateCounselingMutation,
  useUpdateCounselingMutation,
  useDeleteCounselingMutation,
  useGetDomesticAidsQuery,
  useGetDomesticAidQuery,
  useCreateDomesticAidMutation,
  useUpdateDomesticAidMutation,
  useDeleteDomesticAidMutation,
  useGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useGetEmployeeClosuresQuery,
  useGetEmployeeClosureQuery,
  useCreateEmployeeClosureMutation,
  useUpdateEmployeeClosureMutation,
  useDeleteEmployeeClosureMutation,

  useGetEmployeeClosureQueriesQuery,
  useGetEmployeeClosureQueryQuery,

  useGetEmployeeClosureByMonthsQuery,
  useGetEmployeeClosureByMonthQuery,

  useGetWorkTimeEmployeeClosuresCrossReferencesQuery,
  useGetWorkTimeEmployeeClosuresCrossReferenceQuery,
  useCreateWorkTimeEmployeeClosuresCrossReferenceMutation,
  useUpdateWorkTimeEmployeeClosuresCrossReferenceMutation,
  useDeleteWorkTimeEmployeeClosuresCrossReferenceMutation,

  useGetAbsenceEmployeeClosuresCrossReferencesQuery,
  useGetAbsenceEmployeeClosuresCrossReferenceQuery,
  useCreateAbsenceEmployeeClosuresCrossReferenceMutation,
  useUpdateAbsenceEmployeeClosuresCrossReferenceMutation,
  useDeleteAbsenceEmployeeClosuresCrossReferenceMutation,

  useGetInsurancesQuery,
  useGetInsuranceQuery,
  useCreateInsuranceMutation,
  useUpdateInsuranceMutation,
  useDeleteInsuranceMutation,
  useGetInsuranceAddressesQuery,
  useGetInsuranceAddressQuery,
  useCreateInsuranceAddressMutation,
  useUpdateInsuranceAddressMutation,
  useDeleteInsuranceAddressMutation,
  useGetInvoicesQuery,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useGetPublicHolidaysQuery,
  useGetPublicHolidayQuery,
  useCreatePublicHolidayMutation,
  useUpdatePublicHolidayMutation,
  useDeletePublicHolidayMutation,
  useGetSalariesQuery,
  useGetSalaryQuery,
  useCreateSalaryMutation,
  useUpdateSalaryMutation,
  useDeleteSalaryMutation,
  useGetNoVisitReasonsQuery,
  useGetNoVisitReasonQuery,
  useCreateNoVisitReasonMutation,
  useUpdateNoVisitReasonMutation,
  useDeleteNoVisitReasonMutation,
  useGetWorkTimeSignaturesQuery,
  useGetWorkTimeSignatureQuery,
  useCreateWorkTimeSignatureMutation,
  useUpdateWorkTimeSignatureMutation,
  useDeleteWorkTimeSignatureMutation,
  useGetQualificationsQuery,
  useGetQualificationQuery,
  useCreateQualificationMutation,
  useUpdateQualificationMutation,
  useDeleteQualificationMutation,
  useGetCalendarEntriesQuery,
  useGetCalendarEntryQuery,
  useCreateCalendarEntryMutation,
  useUpdateCalendarEntryMutation,
  useDeleteCalendarEntryMutation,
  useGetCalendarSeriesesQuery,
  useGetCalendarSeriesQuery,
  useCreateCalendarSeriesMutation,
  useUpdateCalendarSeriesMutation,
  useDeleteCalendarSeriesMutation,

  useGetLocationsQuery,
  useGetLocationQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
} = plamoApi